import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchMe,
  loginEmail,
  loginTelegram,
  logout as clearToken,
  logoutServer,
  registerEmail,
  updateLocale,
  type AuthResponse,
  type AuthUser,
} from '../api/auth';
import { getAccessToken } from '../api/client';
import { getStoredLocale, isAppLocale, setAppLocale } from '../i18n';
import { applySuggestedCurrencyAfterLogin } from '../lib/geoDefaults';
import { getStoredReferralCode } from '../lib/referralCapture';
import { bootTelegramShell } from '../telegram/webapp';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, acceptTerms: boolean) => Promise<AuthResponse>;
  /** Adopt a session or updated profile returned by another auth call (token already stored). */
  applyUser: (next: AuthUser | null) => void;
  logout: (allDevices?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);


export function AuthProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback((next: AuthUser | null) => {
    setUser(next);
    if (!next) return;
    // Prefer device-stored locale so refresh/login does not silently snap back to AZ.
    const stored = getStoredLocale();
    if (stored) {
      setAppLocale(stored);
      return;
    }
    if (isAppLocale(next.locale)) {
      setAppLocale(next.locale);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      applyUser(null);
      return;
    }
    try {
      const me = await fetchMe();
      applyUser(me);
    } catch {
      clearToken();
      applyUser(null);
    }
  }, [applyUser]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setLoading(true);
      try {
        bootTelegramShell();
        const initData = window.Telegram?.WebApp?.initData;
        if (initData) {
          try {
            await loginTelegram(initData, getStoredReferralCode());
          } catch {
            // Fall through to existing token / anonymous
          }
        }
        if (!cancelled) {
          await refresh();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      refresh,
      applyUser,
      login: async (email, password) => {
        const res = await loginEmail({ email, password });
        applyUser(res.user);
        void applySuggestedCurrencyAfterLogin();
      },
      register: async (email, password, acceptTerms) => {
        const res = await registerEmail({
          email,
          password,
          accept_terms: acceptTerms,
          locale: i18n.language,
          referral_code: getStoredReferralCode(),
        });
        applyUser(res.user);
        void applySuggestedCurrencyAfterLogin();
        return res;
      },
      logout: async (allDevices = false) => {
        try {
          await logoutServer(allDevices);
        } catch {
          // Token is cleared locally either way.
        }
        applyUser(null);
      },
    }),
    [user, loading, refresh, applyUser, i18n.language],
  );

  // Persist language changes for authenticated users
  useEffect(() => {
    if (!user) return;
    const locale = (i18n.resolvedLanguage || i18n.language || 'az').slice(0, 2);
    if (locale && locale !== user.locale) {
      void updateLocale(locale)
        .then((me) => applyUser(me))
        .catch(() => undefined);
    }
  }, [i18n.language, i18n.resolvedLanguage, user, applyUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
